
/**
 * Utility functions for handling media responses (audio and video)
 */

/**
 * Extracts video and audio URLs from response data
 * Works with both object format { videoUrl, audioUrl } and string format
 * 
 * @param responseData The response data from a video question
 * @returns An object with extracted videoUrl and audioUrl
 */
export const extractMediaUrls = (responseData: any): { 
  videoUrl: string | null; 
  audioUrl: string | null;
} => {
  // Default return object
  const result = {
    videoUrl: null,
    audioUrl: null
  };

  // Log for debugging
  console.log('Extracting media URLs from:', {
    type: typeof responseData,
    value: responseData,
    isNull: responseData === null,
    isObject: typeof responseData === 'object' && responseData !== null,
  });

  // Case 1: Response is null or undefined
  if (responseData === null || responseData === undefined) {
    return result;
  }

  // Case 2: Response is already an object with videoUrl and possibly audioUrl
  if (typeof responseData === 'object' && responseData !== null) {
    // Extract videoUrl
    if (typeof responseData.videoUrl === 'string') {
      result.videoUrl = responseData.videoUrl;
    }
    
    // Extract audioUrl if present
    if (typeof responseData.audioUrl === 'string') {
      result.audioUrl = responseData.audioUrl;
    }
    
    console.log('Extracted URLs from object:', result);
    return result;
  }

  // Case 3: Response is a string (legacy format - video URL only)
  if (typeof responseData === 'string') {
    result.videoUrl = responseData;
    console.log('Extracted URL from string:', result);
    return result;
  }

  // Case 4: Try to parse if it's a stringified JSON object
  if (typeof responseData === 'string') {
    try {
      const parsed = JSON.parse(responseData);
      if (typeof parsed === 'object' && parsed !== null) {
        return extractMediaUrls(parsed); // Recursively process the parsed object
      }
    } catch (e) {
      // Not a valid JSON string, already handled by Case 3
    }
  }

  // Return default result for any other case
  console.log('Could not extract URLs, returning defaults:', result);
  return result;
};

/**
 * Extracts a string URL from a JSONB field
 * Handles various formats that might be stored in the JSONB field
 * 
 * @param jsonbField The JSONB field value
 * @returns A string URL or null if extraction fails
 */
export const extractJsonbUrl = (jsonbField: any): string | null => {
  if (!jsonbField) return null;
  
  // If it's already a string, return it directly (new format)
  if (typeof jsonbField === 'string') return jsonbField;
  
  // If it's an object (parsed JSONB - old format)
  if (typeof jsonbField === 'object') {
    // It might be a plain string in a JSON wrapper
    if (Object.keys(jsonbField).length === 0) {
      return null;
    }
    
    // Try to get the value in different ways
    // If it has specific properties that might contain the URL
    if (jsonbField.url) return jsonbField.url;
    if (jsonbField.value) return jsonbField.value;
    if (jsonbField.videoUrl) return jsonbField.videoUrl;
    if (jsonbField.audioUrl) return jsonbField.audioUrl;
    
    // Try JSON.stringify and then strip quotes if it's a simple string
    try {
      const jsonString = JSON.stringify(jsonbField);
      // If it looks like a quoted string, remove the quotes
      if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
        return jsonString.slice(1, -1);
      }
      
      // Try parsing to get the raw string
      const parsed = JSON.parse(jsonString);
      if (typeof parsed === 'string') {
        return parsed;
      }
    } catch (e) {
      console.error('Error extracting URL from JSONB:', e);
    }
    
    // If all else fails, convert to string and check if it's usable
    const stringValue = String(jsonbField);
    if (stringValue && !stringValue.startsWith('[object Object]')) {
      return stringValue;
    }
  }
  
  return null;
};

/**
 * Verifies that URLs were properly stored in the database
 * 
 * @param storedResponse The response data returned from the database
 * @param expectedVideoUrl The expected video URL
 * @param expectedAudioUrl The expected audio URL
 * @returns True if the data was stored correctly
 */
export const verifyMediaStorage = (
  storedResponse: any,
  expectedVideoUrl: string | null,
  expectedAudioUrl: string | null
): boolean => {
  if (!storedResponse) {
    console.error('No stored response data to verify');
    return false;
  }

  // Get the response content (video URL) from the stored response
  const storedResponseContent = storedResponse.response_content;
  
  // Extract audio URL from the JSONB field
  const storedAudioUrl = extractJsonbUrl(storedResponse.audio_url);
  
  // Log for debugging
  console.log('Verifying media storage:', {
    storedResponseContent,
    storedAudioUrl,
    storedAudioUrlRaw: storedResponse.audio_url,
    expectedVideoUrl,
    expectedAudioUrl
  });

  // Check if response_content is correctly set to the video URL
  const responseContentCorrect = expectedVideoUrl === storedResponseContent;
  
  // Check if audio_url is correctly set and extractable
  const audioUrlCorrect = 
    // Either both are null/undefined
    (!expectedAudioUrl && !storedAudioUrl) ||
    // Or the extracted URL matches the expected one
    (!!expectedAudioUrl && expectedAudioUrl === storedAudioUrl);
  
  // Log results
  console.log('Storage verification result:', {
    responseContentCorrect,
    audioUrlCorrect
  });
  
  // Return true only if both checks pass
  return responseContentCorrect && audioUrlCorrect;
};
